import axios from 'axios';
import {
  AdminDevice,
  ActiveDevice,
  ClientGroupOption,
  DeviceCreateRequest,
  DeviceUpdateRequest,
} from '../types/adminDevice';

const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;
const DEVICES_URL = `${API_BASE_URL}/api/admin/devices`;

const authHeaders = (adminApiKey: string) => ({
  headers: {
    'X-Admin-API-Key': adminApiKey,
    'Content-Type': 'application/json',
  },
});

/** Extracts a readable message from an axios error response. */
const extractError = (error: any, fallback: string): Error => {
  if (error.response?.status === 401) {
    return new Error('Invalid admin API key');
  }
  const data = error.response?.data;
  if (data) {
    if (typeof data === 'string') return new Error(data);
    if (data.error) return new Error(data.error);
    if (data.message) return new Error(data.message);
  }
  return new Error(fallback);
};

export const listDevices = async (adminApiKey: string): Promise<AdminDevice[]> => {
  try {
    const response = await axios.get(DEVICES_URL, authHeaders(adminApiKey));
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to load devices');
  }
};

export const listClientGroups = async (adminApiKey: string): Promise<ClientGroupOption[]> => {
  try {
    const response = await axios.get(`${DEVICES_URL}/client-groups`, authHeaders(adminApiKey));
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to load client groups');
  }
};

export const listActiveDevices = async (adminApiKey: string): Promise<ActiveDevice[]> => {
  try {
    const response = await axios.get(`${DEVICES_URL}/active`, authHeaders(adminApiKey));
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to load active devices');
  }
};

export const createDevice = async (
  payload: DeviceCreateRequest,
  adminApiKey: string
): Promise<AdminDevice> => {
  try {
    const response = await axios.post(DEVICES_URL, payload, authHeaders(adminApiKey));
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to create device');
  }
};

export const updateDevice = async (
  serialNo: string,
  payload: DeviceUpdateRequest,
  adminApiKey: string
): Promise<AdminDevice> => {
  try {
    const response = await axios.put(
      `${DEVICES_URL}/${encodeURIComponent(serialNo)}`,
      payload,
      authHeaders(adminApiKey)
    );
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to update device');
  }
};

export const deleteDevice = async (serialNo: string, adminApiKey: string): Promise<void> => {
  try {
    await axios.delete(`${DEVICES_URL}/${encodeURIComponent(serialNo)}`, authHeaders(adminApiKey));
  } catch (error: any) {
    throw extractError(error, 'Failed to delete device');
  }
};

export const setDeviceWatch = async (
  serialNo: string,
  enabled: boolean,
  adminApiKey: string
): Promise<AdminDevice> => {
  try {
    const url = `${DEVICES_URL}/${encodeURIComponent(serialNo)}/watch`;
    const response = enabled
      ? await axios.post(url, {}, authHeaders(adminApiKey))
      : await axios.delete(url, authHeaders(adminApiKey));
    return response.data;
  } catch (error: any) {
    throw extractError(error, 'Failed to update watch status');
  }
};
